import React, { useState, useEffect } from "react";
import {
  FolderOpen,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Download,
  Trash2,
  Search,
  Filter,
  User,
  ExternalLink,
  MessageSquare,
  Save,
  Loader2,
  RefreshCw,
  Layers,
  Heart,
  Baby,
  Users,
  UploadCloud,
  Share2,
  Copy,
  Check,
  Phone,
  Hash,
  X,
} from "lucide-react";
import { api, API_BASE_URL } from "../../lib/api";
import { useLang } from "../../context/LanguageContext";

const FAMILY_ROLES = [
  { id: "main_applicant", fr: "Demandeur Principal", en: "Principal Applicant", icon: User, color: "text-blue-700 bg-blue-50 border-blue-200" },
  { id: "spouse", fr: "Conjoint(e) / Époux(se)", en: "Spouse / Partner", icon: Heart, color: "text-purple-700 bg-purple-50 border-purple-200" },
  { id: "child", fr: "Enfant à charge", en: "Dependent Child", icon: Baby, color: "text-amber-700 bg-amber-50 border-amber-200" },
  { id: "other", fr: "Autre membre", en: "Other Member", icon: Users, color: "text-slate-700 bg-slate-50 border-slate-200" },
];

export default function AdminDocumentsManager() {
  const { lang } = useLang();
  const [documents, setDocuments] = useState([]);
  const [clientSummaries, setClientSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClientId, setSelectedClientId] = useState("all");
  const [selectedFamilyRole, setSelectedFamilyRole] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingNotes, setEditingNotes] = useState({});
  const [savingStatusId, setSavingStatusId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [shareClientName, setShareClientName] = useState("");
  const [shareRef, setShareRef] = useState("");

  const getDropBoxUrl = () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    let url = `${origin}/depot-documents`;
    const params = new URLSearchParams();
    if (shareClientName.trim()) params.set("name", shareClientName.trim());
    if (shareRef.trim()) params.set("ref", shareRef.trim());
    const qs = params.toString();
    return qs ? `${url}?${qs}` : url;
  };

  const handleCopyLink = () => {
    const url = getDropBoxUrl();
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const handleShareWhatsApp = () => {
    const url = getDropBoxUrl();
    const text =
      lang === "fr"
        ? `Bonjour ${shareClientName.trim() ? shareClientName.trim() + " " : ""}, voici votre lien direct et sécurisé pour déposer vos documents d'immigration JNIC (aucun mot de passe requis) : ${url}`
        : `Hello ${shareClientName.trim() ? shareClientName.trim() + " " : ""}, here is your secure direct link to upload your JNIC immigration documents (no sign-in required): ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [docsRes, summaryRes] = await Promise.all([
        api.get("/admin/documents"),
        api.get("/admin/documents/clients-summary"),
      ]);
      setDocuments(Array.isArray(docsRes.data) ? docsRes.data : []);
      setClientSummaries(Array.isArray(summaryRes.data) ? summaryRes.data : []);

      // initialize editing notes
      const notesMap = {};
      (docsRes.data || []).forEach((d) => {
        notesMap[d.doc_id] = d.admin_notes || "";
      });
      setEditingNotes(notesMap);
    } catch (err) {
      console.error("Failed to load admin documents:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleUpdateStatus = async (docId, newStatus) => {
    try {
      setSavingStatusId(docId);
      const currentNote = editingNotes[docId] || "";
      const res = await api.put(`/admin/documents/${docId}/status`, {
        status: newStatus,
        admin_notes: currentNote,
      });

      setDocuments((prev) =>
        prev.map((d) => (d.doc_id === docId ? { ...d, status: res.data.status, admin_notes: res.data.admin_notes } : d))
      );
      // Reload summary stats
      const summaryRes = await api.get("/admin/documents/clients-summary");
      setClientSummaries(Array.isArray(summaryRes.data) ? summaryRes.data : []);
    } catch (err) {
      alert(lang === "fr" ? "Erreur lors de la mise à jour du statut." : "Error updating status.");
    } finally {
      setSavingStatusId(null);
    }
  };

  const handleSaveNotes = async (docId) => {
    const doc = documents.find((d) => d.doc_id === docId);
    if (!doc) return;
    await handleUpdateStatus(docId, doc.status || "pending_review");
  };

  const handleDelete = async (docId) => {
    if (!window.confirm(lang === "fr" ? "Voulez-vous supprimer définitivement ce document du stockage ?" : "Do you want to permanently delete this document from storage?")) return;
    try {
      setDeletingId(docId);
      await api.delete(`/documents/${docId}`);
      setDocuments((prev) => prev.filter((d) => d.doc_id !== docId));
      const summaryRes = await api.get("/admin/documents/clients-summary");
      setClientSummaries(Array.isArray(summaryRes.data) ? summaryRes.data : []);
    } catch (err) {
      alert(lang === "fr" ? "Erreur lors de la suppression." : "Error deleting document.");
    } finally {
      setDeletingId(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return lang === "fr" ? "0 Ko" : "0 KB";
    const k = 1024;
    const dm = 1;
    const sizes = lang === "fr" ? ["Octets", "Ko", "Mo", "Go"] : ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const getFamilyBadge = (doc) => {
    const role = FAMILY_ROLES.find((r) => r.id === (doc.family_member || "main_applicant")) || FAMILY_ROLES[0];
    const Icon = role.icon;
    const nameExtra = doc.family_member_name ? ` : ${doc.family_member_name}` : "";
    return (
      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full border ${role.color}`}>
        <Icon size={12} />
        <span>{lang === "fr" ? role.fr : role.en}{nameExtra}</span>
      </span>
    );
  };

  // Filtering
  const filteredDocuments = documents.filter((doc) => {
    if (selectedClientId !== "all" && doc.user_id !== selectedClientId) return false;
    if (selectedFamilyRole !== "all" && (doc.family_member || "main_applicant") !== selectedFamilyRole) return false;
    if (statusFilter !== "all" && doc.status !== statusFilter) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchName = (doc.user_name || "").toLowerCase().includes(q);
      const matchEmail = (doc.user_email || "").toLowerCase().includes(q);
      const matchTitle = (doc.custom_title || "").toLowerCase().includes(q);
      const matchMemberName = (doc.family_member_name || "").toLowerCase().includes(q);
      const matchCat = (doc.document_category_label_fr || "").toLowerCase().includes(q);
      if (!matchName && !matchEmail && !matchTitle && !matchMemberName && !matchCat) return false;
    }
    return true;
  });

  const totalDocs = documents.length;
  const pendingCount = documents.filter((d) => d.status === "pending_review").length;
  const approvedCount = documents.filter((d) => d.status === "approved").length;
  const actionRequiredCount = documents.filter((d) => d.status === "action_required").length;

  return (
    <div className="space-y-6" data-testid="admin-documents-manager">
      {/* Top Stats Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-navy/10 shadow-2xs">
          <p className="text-xs font-semibold text-navy/60 uppercase tracking-wider">{lang === "fr" ? "Total Documents" : "Total Documents"}</p>
          <p className="text-3xl font-serif font-bold text-navy mt-1">{totalDocs}</p>
          <p className="text-[11px] text-navy/40 mt-1">{clientSummaries.length} {lang === "fr" ? "dossiers clients" : "client files"}</p>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-blue-200 bg-blue-50/20 shadow-2xs">
          <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider">{lang === "fr" ? "En attente de révision" : "Pending Review"}</p>
          <p className="text-3xl font-serif font-bold text-blue-700 mt-1">{pendingCount}</p>
          <p className="text-[11px] text-blue-600/70 mt-1">{lang === "fr" ? "À examiner" : "To review"}</p>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-emerald-200 bg-emerald-50/20 shadow-2xs">
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">{lang === "fr" ? "Validés IRCC" : "IRCC Approved"}</p>
          <p className="text-3xl font-serif font-bold text-emerald-700 mt-1">{approvedCount}</p>
          <p className="text-[11px] text-emerald-600/70 mt-1">{lang === "fr" ? "Conformes aux exigences" : "Compliant with requirements"}</p>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-amber-200 bg-amber-50/20 shadow-2xs">
          <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider">{lang === "fr" ? "Corrections demandées" : "Action Required"}</p>
          <p className="text-3xl font-serif font-bold text-amber-700 mt-1">{actionRequiredCount}</p>
          <p className="text-[11px] text-amber-600/70 mt-1">{lang === "fr" ? "En attente client" : "Awaiting client"}</p>
        </div>
      </div>

      {/* DropBox Link Generator & Share Banner */}
      <div className="bg-gradient-to-r from-navy to-slate-900 text-white rounded-2xl p-6 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-gold/20 text-gold flex items-center justify-center shrink-0 border border-gold/30">
            <UploadCloud size={22} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-serif text-lg font-bold text-white">
                {lang === "fr" ? "Lien Dépôt Sécurisé (DropBox Client)" : "Client Secure DropBox Link"}
              </h3>
              <span className="text-[10px] uppercase tracking-wider font-bold bg-gold/20 text-gold px-2 py-0.5 rounded-full border border-gold/30">
                {lang === "fr" ? "Sans connexion requise" : "No sign-in required"}
              </span>
            </div>
            <p className="text-xs text-white/70 mt-1 max-w-xl">
              {lang === "fr"
                ? "Envoyez ce lien à vos clients par WhatsApp ou email pour qu'ils déposent leurs pièces justificatives en quelques secondes avec des intitulés significatifs."
                : "Send this link to clients via WhatsApp or email so they can upload supporting documents in seconds with clear titles."}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleCopyLink}
            className="px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition flex items-center gap-2 border border-white/15"
          >
            {copiedLink ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
            <span>{copiedLink ? (lang === "fr" ? "Lien copié !" : "Link copied!") : (lang === "fr" ? "Copier le lien" : "Copy link")}</span>
          </button>

          <button
            type="button"
            onClick={() => setShowShareModal(true)}
            className="px-4 py-2.5 rounded-xl bg-gold hover:bg-gold/90 text-navy text-xs font-bold transition flex items-center gap-2 shadow-sm"
          >
            <Share2 size={14} />
            <span>{lang === "fr" ? "Personnaliser & Partager" : "Customize & Share"}</span>
          </button>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-7 shadow-2xl border border-navy/10 animate-fade-in">
            <div className="flex items-center justify-between pb-3 mb-4 border-b border-navy/10">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-navy text-gold flex items-center justify-center">
                  <UploadCloud size={18} />
                </div>
                <h4 className="font-serif text-lg font-bold text-navy">
                  {lang === "fr" ? "Partager la Boîte de Dépôt JNIC" : "Share JNIC DropBox"}
                </h4>
              </div>
              <button
                type="button"
                onClick={() => setShowShareModal(false)}
                className="p-1.5 text-navy/40 hover:text-navy rounded-lg transition"
              >
                <X size={18} />
              </button>
            </div>

            <p className="text-xs text-navy/60 leading-relaxed mb-4">
              {lang === "fr"
                ? "Personnalisez le lien avec le nom du client et sa référence afin qu'ils soient déjà pré-remplis sur son écran lors de l'ouverture du lien."
                : "Tailor the link with client name and reference so they are already pre-filled when the client opens it."}
            </p>

            <div className="space-y-3 mb-5">
              <div>
                <label className="block text-[11px] font-bold text-navy uppercase tracking-wider mb-1">
                  {lang === "fr" ? "Nom du client (Optionnel, pour pré-remplissage) :" : "Client Name (Optional) :"}
                </label>
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
                  <input
                    type="text"
                    value={shareClientName}
                    onChange={(e) => setShareClientName(e.target.value)}
                    placeholder="Ex. Jean Dupont"
                    className="w-full h-10 pl-9 pr-3 rounded-xl border border-navy/15 text-xs text-navy focus:outline-none focus:border-redcanada"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-navy uppercase tracking-wider mb-1">
                  {lang === "fr" ? "Référence ou programme (Optionnel) :" : "Reference or Program (Optional) :"}
                </label>
                <div className="relative">
                  <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
                  <input
                    type="text"
                    value={shareRef}
                    onChange={(e) => setShareRef(e.target.value)}
                    placeholder="Ex. Entrée Express 2026"
                    className="w-full h-10 pl-9 pr-3 rounded-xl border border-navy/15 text-xs text-navy focus:outline-none focus:border-redcanada"
                  />
                </div>
              </div>

              <div className="pt-2">
                <label className="block text-[11px] font-bold text-navy uppercase tracking-wider mb-1">
                  {lang === "fr" ? "Lien direct généré :" : "Generated direct link :"}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={getDropBoxUrl()}
                    className="flex-1 h-10 px-3 rounded-xl bg-slate-50 border border-navy/15 text-xs font-mono text-navy select-all"
                  />
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="h-10 px-3.5 rounded-xl bg-navy text-white text-xs font-bold hover:bg-redcanada transition flex items-center gap-1.5 shrink-0"
                  >
                    {copiedLink ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                    <span>{copiedLink ? (lang === "fr" ? "Copié !" : "Copied!") : (lang === "fr" ? "Copier" : "Copy")}</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-2 pt-3 border-t border-navy/10">
              <button
                type="button"
                onClick={handleShareWhatsApp}
                className="w-full sm:flex-1 h-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition flex items-center justify-center gap-2 shadow-xs"
              >
                <span>💬 {lang === "fr" ? "Partager sur WhatsApp" : "Share on WhatsApp"}</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  const subject = encodeURIComponent(
                    lang === "fr"
                      ? "JNIC : Lien de dépôt sécurisé de vos documents"
                      : "JNIC: Secure document upload link"
                  );
                  const body = encodeURIComponent(
                    lang === "fr"
                      ? `Bonjour ${shareClientName.trim() ? shareClientName.trim() + " " : ""},\n\nVoici votre lien sécurisé pour nous transmettre vos pièces justificatives directement (sans création de compte requise) :\n\n${getDropBoxUrl()}\n\nMerci,\nCabinet JN Immigration Consulting`
                      : `Hello ${shareClientName.trim() ? shareClientName.trim() + " " : ""},\n\nHere is your secure link to upload your supporting documents directly (no account required):\n\n${getDropBoxUrl()}\n\nBest regards,\nJN Immigration Consulting`
                  );
                  window.location.href = `mailto:?subject=${subject}&body=${body}`;
                }}
                className="w-full sm:flex-1 h-10 rounded-xl border border-navy/20 hover:bg-navy/5 text-navy text-xs font-bold transition flex items-center justify-center gap-2"
              >
                <span>✉️ {lang === "fr" ? "Envoyer par Email" : "Send via Email"}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client Folders Row */}
      <div className="bg-white rounded-2xl border border-navy/10 p-6 shadow-2xs">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-2">
            <FolderOpen className="text-terracotta" size={20} />
            <h3 className="font-serif text-lg font-bold text-navy">{lang === "fr" ? "Dossiers Clients & Familles Immigrantes" : "Client Files & Immigrant Families"}</h3>
          </div>
          <button
            type="button"
            onClick={loadData}
            className="p-2 rounded-lg text-navy/60 hover:text-navy hover:bg-navy/5 transition flex items-center gap-1.5 text-xs font-medium"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            <span>{lang === "fr" ? "Actualiser" : "Refresh"}</span>
          </button>
        </div>

        {clientSummaries.length === 0 ? (
          <p className="text-sm text-navy/50 italic py-4">{lang === "fr" ? "Aucun dossier client téléversé pour le moment." : "No client files uploaded yet."}</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <button
              type="button"
              onClick={() => setSelectedClientId("all")}
              className={`p-3.5 rounded-xl border text-left transition-all ${
                selectedClientId === "all"
                  ? "bg-navy text-white border-navy shadow-xs"
                  : "bg-alabaster hover:bg-white border-navy/10 text-navy"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">{lang === "fr" ? "Tous les dossiers clients" : "All client files"}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${selectedClientId === "all" ? "bg-white/20 text-white" : "bg-navy/10 text-navy"}`}>
                  {totalDocs}
                </span>
              </div>
            </button>

            {clientSummaries.map((client) => (
              <button
                key={client.user_id}
                type="button"
                onClick={() => setSelectedClientId(client.user_id)}
                className={`p-3.5 rounded-xl border text-left transition-all ${
                  selectedClientId === client.user_id
                    ? "bg-navy text-white border-navy shadow-xs"
                    : "bg-alabaster hover:bg-white border-navy/10 text-navy"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{client.user_name}</p>
                    <p className={`text-xs truncate ${selectedClientId === client.user_id ? "text-white/70" : "text-navy/50"}`}>
                      {client.user_email}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${selectedClientId === client.user_id ? "bg-white/20 text-white" : "bg-navy/10 text-navy"}`}>
                      {client.total_documents} doc{client.total_documents > 1 ? "s" : ""}
                    </span>
                    {client.pending_count > 0 && (
                      <span className="text-[10px] font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                        {client.pending_count} {lang === "fr" ? "à réviser" : "to review"}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white rounded-2xl border border-navy/10 p-4 shadow-2xs flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="relative w-full sm:w-80">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-navy/40" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={lang === "fr" ? "Rechercher par client, email, nom d'enfant, titre..." : "Search by client, email, child name, title..."}
              className="w-full pl-9 pr-4 py-2 rounded-xl border border-navy/15 text-xs focus:outline-hidden focus:border-terracotta"
            />
          </div>

          {/* Status Pills */}
          <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
            {[
              { id: "all", label: lang === "fr" ? "Tous les statuts" : "All statuses" },
              { id: "pending_review", label: lang === "fr" ? "En attente" : "Pending" },
              { id: "approved", label: lang === "fr" ? "Validés" : "Approved" },
              { id: "action_required", label: lang === "fr" ? "À corriger" : "Action required" },
            ].map((st) => (
              <button
                key={st.id}
                type="button"
                onClick={() => setStatusFilter(st.id)}
                className={`px-3 py-1.5 rounded-xl text-xs font-medium whitespace-nowrap transition ${
                  statusFilter === st.id
                    ? "bg-terracotta text-white shadow-xs"
                    : "bg-navy/5 text-navy/70 hover:bg-navy/10"
                }`}
              >
                {st.label}
              </button>
            ))}
          </div>
        </div>

        {/* Family Member Filter Pills for Admin */}
        <div className="flex items-center gap-1.5 pt-2 border-t border-navy/5 overflow-x-auto">
          <span className="text-[11px] font-semibold text-navy/50 uppercase tracking-wider pr-1 shrink-0">{lang === "fr" ? "Membre :" : "Member:"}</span>
          <button
            type="button"
            onClick={() => setSelectedFamilyRole("all")}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium transition shrink-0 ${
              selectedFamilyRole === "all"
                ? "bg-navy text-white"
                : "bg-navy/5 text-navy/70 hover:bg-navy/10"
            }`}
          >
            {lang === "fr" ? "Tous les membres" : "All members"}
          </button>
          {FAMILY_ROLES.map((role) => {
            const Icon = role.icon;
            return (
              <button
                key={role.id}
                type="button"
                onClick={() => setSelectedFamilyRole(role.id)}
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition shrink-0 ${
                  selectedFamilyRole === role.id
                    ? "bg-navy text-white"
                    : "bg-navy/5 text-navy/70 hover:bg-navy/10"
                }`}
              >
                <Icon size={12} />
                <span>{lang === "fr" ? role.fr : role.en}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Documents List */}
      <div className="space-y-4">
        {filteredDocuments.length === 0 ? (
          <div className="bg-white rounded-2xl border border-navy/10 p-12 text-center">
            <FileText size={36} className="mx-auto text-navy/20 mb-3" />
            <p className="text-navy/60 font-medium text-sm">{lang === "fr" ? "Aucun document ne correspond à vos filtres." : "No documents match your filters."}</p>
          </div>
        ) : (
          filteredDocuments.map((doc) => (
            <div
              key={doc.doc_id}
              className="bg-white rounded-2xl border border-navy/10 p-5 shadow-2xs space-y-4 hover:border-navy/25 transition-all"
            >
              {/* Top Row: File Details & Uploader Info */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="p-3 bg-navy/5 text-navy rounded-xl shrink-0 mt-0.5">
                    <FileText size={22} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {getFamilyBadge(doc)}
                      <span className="text-[11px] font-semibold bg-navy/10 text-navy px-2.5 py-0.5 rounded-full">
                        {lang === "fr" ? (doc.document_category_label_fr || doc.document_type) : (doc.document_category_label_en || doc.document_category_label_fr || doc.document_type)}
                      </span>
                      {doc.upload_source === "public_dropbox" && (
                        <span className="text-[11px] font-bold bg-purple-100 text-purple-800 border border-purple-200 px-2.5 py-0.5 rounded-full flex items-center gap-1">
                          <UploadCloud size={11} />
                          <span>{lang === "fr" ? "Dépôt DropBox (Sans compte)" : "DropBox Upload"}</span>
                        </span>
                      )}
                    </div>

                    <h4 className="font-bold text-navy text-base truncate" title={doc.custom_title || doc.original_filename}>
                      {doc.custom_title || doc.original_filename}
                    </h4>

                    <div className="flex items-center gap-3 text-xs text-navy/60 mt-1 flex-wrap">
                      <span className="font-semibold text-navy flex items-center gap-1">
                        <User size={13} className="text-terracotta" /> {doc.user_name} ({doc.user_email})
                      </span>
                      {doc.client_phone && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1 text-emerald-700 font-medium">
                            <Phone size={12} /> {doc.client_phone}
                          </span>
                        </>
                      )}
                      {doc.file_reference && (
                        <>
                          <span>•</span>
                          <span className="flex items-center gap-1 text-blue-700 font-medium">
                            <Hash size={12} /> {doc.file_reference}
                          </span>
                        </>
                      )}
                      <span>•</span>
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span>•</span>
                      <span>{new Date(doc.created_at || doc.uploaded_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA")}</span>
                    </div>

                    {doc.client_notes && (
                      <p className="mt-2 text-xs text-navy/80 bg-amber-50/70 p-2.5 rounded-xl border border-amber-200/60 leading-relaxed">
                        <span className="font-bold text-amber-900">{lang === "fr" ? "Remarque du client :" : "Client note:"} </span>
                        {doc.client_notes}
                      </p>
                    )}
                  </div>
                </div>

                {/* Status Indicator & View Button */}
                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                  {doc.status === "approved" && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200">
                      <CheckCircle2 size={14} /> {lang === "fr" ? "Validé" : "Approved"}
                    </span>
                  )}
                  {doc.status === "pending_review" && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200">
                      <Clock size={14} /> {lang === "fr" ? "En attente de révision" : "Pending review"}
                    </span>
                  )}
                  {doc.status === "action_required" && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200">
                      <AlertTriangle size={14} /> {lang === "fr" ? "Correction demandée" : "Correction requested"}
                    </span>
                  )}

                  <a
                    href={`${API_BASE_URL}/documents/download/${doc.doc_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-semibold bg-navy text-white hover:bg-terracotta transition shadow-xs"
                  >
                    <Download size={14} />
                    <span>{lang === "fr" ? "Télécharger / Ouvrir" : "Download / Open"}</span>
                  </a>

                  <button
                    type="button"
                    onClick={() => handleDelete(doc.doc_id)}
                    disabled={deletingId === doc.doc_id}
                    className="p-2 rounded-xl text-red-500 hover:bg-red-50 transition"
                    title={lang === "fr" ? "Supprimer" : "Delete"}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Review & Notes Action Row */}
              <div className="pt-3 border-t border-navy/10 flex flex-col md:flex-row md:items-center justify-between gap-3 bg-alabaster/60 -mx-5 -mb-5 p-4 rounded-b-2xl">
                {/* Note input */}
                <div className="flex-1 flex items-center gap-2">
                  <MessageSquare size={16} className="text-navy/40 shrink-0" />
                  <input
                    type="text"
                    value={editingNotes[doc.doc_id] ?? (doc.admin_notes || "")}
                    onChange={(e) =>
                      setEditingNotes({ ...editingNotes, [doc.doc_id]: e.target.value })
                    }
                    placeholder={lang === "fr" ? "Ajouter une instruction ou remarque pédagogique pour ce document de la famille..." : "Add guidance or instruction for this family document..."}
                    className="w-full px-3 py-1.5 text-xs bg-white rounded-xl border border-navy/20 focus:outline-hidden focus:border-terracotta"
                  />
                  <button
                    type="button"
                    onClick={() => handleSaveNotes(doc.doc_id)}
                    disabled={savingStatusId === doc.doc_id}
                    className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-navy/10 hover:bg-navy text-navy hover:text-white transition shrink-0 flex items-center gap-1"
                  >
                    <Save size={12} />
                    <span>{lang === "fr" ? "Sauvegarder" : "Save"}</span>
                  </button>
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => handleUpdateStatus(doc.doc_id, "approved")}
                    disabled={savingStatusId === doc.doc_id}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition flex items-center gap-1.5 ${
                      doc.status === "approved"
                        ? "bg-emerald-600 text-white"
                        : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200"
                    }`}
                  >
                    <CheckCircle2 size={13} />
                    <span>{lang === "fr" ? "Valider conforme" : "Validate approved"}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleUpdateStatus(doc.doc_id, "action_required")}
                    disabled={savingStatusId === doc.doc_id}
                    className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition flex items-center gap-1.5 ${
                      doc.status === "action_required"
                        ? "bg-amber-600 text-white"
                        : "bg-amber-50 text-amber-800 hover:bg-amber-100 border border-amber-200"
                    }`}
                  >
                    <AlertTriangle size={13} />
                    <span>{lang === "fr" ? "Demander correction" : "Request revision"}</span>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
